<template>
  <AppLayout page-title="Job Details">
    <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
      <!-- Animated Background -->
      <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
      <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
      
      <!-- Floating Particles -->
      <div class="fixed inset-0 pointer-events-none">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>

      <div class="relative z-10 p-8">
        <!-- Back Navigation -->
        <div class="mb-6">
          <router-link to="/jobs" class="inline-flex items-center text-emerald-400 hover:text-emerald-300 transition-colors">
            <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
            </svg>
            Back to Job Marketplace
          </router-link>
        </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-12">
        <div class="animate-spin w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p class="text-oil-600">Loading job details...</p>
      </div>

      <!-- Job Details -->
      <div v-else-if="job" class="space-y-6">
        <!-- Job Header -->
        <div class="card">
          <div class="card-body">
            <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between">
              <div class="flex-1">
                <div class="flex items-start justify-between mb-4">
                  <div>
                    <h1 class="text-3xl font-bold text-oil-900 mb-2">{{ job.title }}</h1>
                    <div class="flex items-center space-x-4 text-oil-600">
                      <div class="flex items-center">
                        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                        </svg>
                        {{ job.location.address.city }}, {{ job.location.address.state }}
                      </div>
                      <div class="flex items-center">
                        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                        </svg>
                        {{ job.company.companyName }}
                      </div>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-4xl font-bold text-success-600">${{ job.payRate.hourly }}/hr</div>
                    <div class="text-lg text-oil-600">{{ job.payRate.daily ? `$${job.payRate.daily}/day` : '' }}</div>
                  </div>
                </div>

                <!-- Job Status and Actions -->
                <div class="flex flex-wrap items-center gap-4 mt-6">
                  <span class="badge-primary">{{ job.jobType.replace('_', ' ').toUpperCase() }}</span>
                  <span class="rotation-badge">{{ job.schedule.rotation }}</span>
                  <span class="badge-oil">{{ job.schedule.shiftLength }}hr shifts</span>
                  <span v-if="job.schedule.breakTime" class="badge-oil">{{ job.schedule.breakTime }}min breaks</span>
                  <span :class="getStatusBadgeClass(job.status)" class="badge">
                    {{ job.status.replace('_', ' ').toUpperCase() }}
                  </span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="lg:ml-6 mt-6 lg:mt-0 flex flex-col space-y-3">
                <button
                  @click="applyToJob"
                  :disabled="job.hasApplied || !isAuthenticated"
                  :class="job.hasApplied ? 'btn-outline opacity-50 cursor-not-allowed' : 'btn-success'"
                  class="btn btn-lg"
                >
                  <svg v-if="!job.hasApplied" class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {{ job.hasApplied ? 'Already Applied' : 'Apply Now' }}
                </button>
                
                <button
                  @click="saveJob"
                  :class="job.isSaved ? 'btn-outline bg-primary-50 text-primary-700' : 'btn-outline'"
                  class="btn btn-lg"
                >
                  <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="!job.isSaved" d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z"/>
                    <path v-else fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
                  </svg>
                  {{ job.isSaved ? 'Saved' : 'Save Job' }}
                </button>

                <button @click="shareJob" class="btn-outline btn-lg">
                  <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"/>
                  </svg>
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Job Details Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Main Content -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Job Description -->
            <div class="card">
              <div class="card-header">
                <h2 class="text-xl font-semibold text-oil-900">Job Description</h2>
              </div>
              <div class="card-body">
                <p class="text-oil-700 leading-relaxed">{{ job.description }}</p>
                
                <!-- Additional Details -->
                <div class="mt-6 space-y-4">
                  <div v-if="job.schedule.overtimeRules" class="flex items-start">
                    <svg class="w-5 h-5 text-safety-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                    </svg>
                    <div>
                      <h4 class="font-medium text-oil-900">Overtime Rules</h4>
                      <p class="text-oil-600">{{ job.schedule.overtimeRules }}</p>
                    </div>
                  </div>

                  <div v-if="job.location.wellName || job.location.padName" class="flex items-start">
                    <svg class="w-5 h-5 text-primary-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                    </svg>
                    <div>
                      <h4 class="font-medium text-oil-900">Location Details</h4>
                      <p class="text-oil-600">
                        <span v-if="job.location.wellName">Well: {{ job.location.wellName }}</span>
                        <span v-if="job.location.padName && job.location.wellName"> • </span>
                        <span v-if="job.location.padName">Pad: {{ job.location.padName }}</span>
                        <span v-if="job.location.fieldName"> • Field: {{ job.location.fieldName }}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Requirements -->
            <div class="card">
              <div class="card-header">
                <h2 class="text-xl font-semibold text-oil-900">Requirements & Qualifications</h2>
              </div>
              <div class="card-body">
                <div v-if="job.requirements.length > 0" class="space-y-4">
                  <div v-for="req in job.requirements" :key="req.certificationType" class="flex items-start p-4 bg-oil-50 rounded-lg">
                    <div class="flex-1">
                      <h4 class="font-medium text-oil-900 mb-1">
                        {{ formatCertificationType(req.certificationType) }}
                      </h4>
                      <div class="flex items-center space-x-4 text-sm text-oil-600">
                        <span>Level: {{ req.skillLevel }}</span>
                        <span>Experience: {{ req.yearsExperience }} years</span>
                        <span class="badge-success">{{ req.isRequired ? 'Required' : 'Preferred' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-8 text-oil-500">
                  <p>No specific requirements listed</p>
                </div>
              </div>
            </div>

            <!-- Equipment -->
            <div v-if="job.equipment.length > 0" class="card">
              <div class="card-header">
                <h2 class="text-xl font-semibold text-oil-900">Equipment & Tools</h2>
              </div>
              <div class="card-body">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div v-for="equip in job.equipment" :key="equip.id" class="flex items-center p-3 bg-oil-50 rounded-lg">
                    <div class="flex-1">
                      <h4 class="font-medium text-oil-900">{{ equip.name }}</h4>
                      <p class="text-sm text-oil-600">
                        Qty: {{ equip.quantity }} • 
                        {{ equip.isProvided ? 'Provided' : 'Bring Your Own' }}
                      </p>
                      <p v-if="equip.specifications" class="text-xs text-oil-500 mt-1">
                        {{ equip.specifications }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="space-y-6">
            <!-- Company Info -->
            <div class="card">
              <div class="card-header">
                <h3 class="text-lg font-semibold text-oil-900">Company Information</h3>
              </div>
              <div class="card-body">
                <div class="text-center">
                  <div class="w-16 h-16 bg-gradient-to-br from-primary-500 to-oil-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span class="text-xl font-bold text-white">
                      {{ job.company.companyName.charAt(0) }}
                    </span>
                  </div>
                  <h4 class="font-semibold text-oil-900 mb-2">{{ job.company.companyName }}</h4>
                  <p class="text-oil-600 text-sm mb-4">Oilfield Services Company</p>
                  
                  <div class="space-y-2 text-sm">
                    <div class="flex items-center justify-between">
                      <span class="text-oil-600">Company Rating:</span>
                      <div class="flex items-center">
                        <span class="text-safety-600 font-medium">4.8</span>
                        <svg class="w-4 h-4 text-safety-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      </div>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-oil-600">Active Jobs:</span>
                      <span class="font-medium">12</span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-oil-600">Hired This Month:</span>
                      <span class="font-medium">8</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Job Schedule -->
            <div class="card">
              <div class="card-header">
                <h3 class="text-lg font-semibold text-oil-900">Schedule & Timeline</h3>
              </div>
              <div class="card-body">
                <div class="space-y-4">
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Start Date:</span>
                    <span class="font-medium">{{ formatDate(job.startDate) }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">End Date:</span>
                    <span class="font-medium">{{ job.endDate ? formatDate(job.endDate) : 'Ongoing' }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Rotation:</span>
                    <span class="font-medium">{{ job.schedule.rotation }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Shift Length:</span>
                    <span class="font-medium">{{ job.schedule.shiftLength }} hours</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Break Time:</span>
                    <span class="font-medium">{{ job.schedule.breakTime }} minutes</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="card">
              <div class="card-header">
                <h3 class="text-lg font-semibold text-oil-900">Job Statistics</h3>
              </div>
              <div class="card-body">
                <div class="space-y-4">
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Applications:</span>
                    <span class="font-medium">{{ job.applications.length }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Days Posted:</span>
                    <span class="font-medium">{{ getDaysPosted(job.createdAt) }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-oil-600">Views:</span>
                    <span class="font-medium">247</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Job Not Found -->
      <div v-else class="text-center py-12">
        <svg class="w-16 h-16 text-oil-300 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
        <h3 class="text-lg font-medium text-oil-900 mb-2">Job Not Found</h3>
        <p class="text-oil-600 mb-4">The job you're looking for doesn't exist or has been removed.</p>
        <router-link to="/jobs" class="btn-primary">
          Browse Other Jobs
        </router-link>
      </div>
      </div> <!-- Close relative z-10 p-8 -->
    </div> <!-- Close min-h-screen bg-gradient -->
  </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from '@/components/layout/AppLayout.vue'
import type { Job } from '@/types'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

// State
const job = ref<Job | null>(null)
const isLoading = ref(true)
const isAuthenticated = computed(() => !!localStorage.getItem('auth_token'))

// Methods
const loadJob = async () => {
  try {
    // Mock API call - replace with actual API
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Mock job data - replace with actual API call
    job.value = {
      id: route.params.id as string,
      title: 'Flowback Operator',
      description: 'Experienced flowback operator needed for Bakken operations. Must have PEC SafeLand and H₂S certifications. Experience with flowback units and test separators required. This position involves operating and monitoring flowback equipment, managing well flow rates, and ensuring safe operations during well completion and testing phases.',
      company: { companyName: 'Bakken Energy Services', id: '1' } as any,
      location: { 
        address: { city: 'Williston', state: 'ND' },
        wellName: 'Bakken-42',
        padName: 'Pad 7',
        fieldName: 'Bakken Formation'
      } as any,
      jobType: 'flowback',
      status: 'posted',
      requirements: [
        { certificationType: 'pec_safeland', skillCategory: 'flowback', skillLevel: 'intermediate', yearsExperience: 2, isRequired: true },
        { certificationType: 'h2s', skillCategory: 'safety', skillLevel: 'intermediate', yearsExperience: 1, isRequired: true },
        { certificationType: 'first_aid', skillCategory: 'safety', skillLevel: 'beginner', yearsExperience: 0, isRequired: false }
      ],
      schedule: { 
        startDate: new Date('2024-02-01'), 
        endDate: new Date('2024-06-01'),
        rotation: '2w1o', 
        shiftLength: 12, 
        breakTime: 30, 
        overtimeRules: 'After 40 hours, overtime at 1.5x rate' 
      },
      payRate: { hourly: 45, daily: 540, currency: 'USD' },
      equipment: [
        { id: '1', name: 'Flowback Unit', type: 'flowback_unit', quantity: 1, isProvided: true, specifications: 'High-pressure rated' },
        { id: '2', name: 'Test Separator', type: 'test_separator', quantity: 1, isProvided: true },
        { id: '3', name: 'Safety Equipment', type: 'ppe', quantity: 1, isProvided: true, specifications: 'Hard hat, safety glasses, steel-toe boots' }
      ],
      safetyRequirements: [],
      applications: [],
      assignedContractors: [],
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date(),
      startDate: new Date('2024-02-01'),
      hasApplied: false,
      isSaved: false
    } as any
  } catch (error) {
    console.error('Error loading job:', error)
  } finally {
    isLoading.value = false
  }
}

const applyToJob = () => {
  if (!isAuthenticated.value) {
    router.push('/login')
    return
  }
  
  // Mock application - replace with actual API call
  if (job.value) {
    job.value.hasApplied = true
    console.log(`Applied to ${job.value.title}`)
  }
}

const saveJob = () => {
  if (job.value) {
    job.value.isSaved = !job.value.isSaved
    console.log(job.value.isSaved ? `Saved ${job.value.title}` : `Removed ${job.value.title} from saved jobs`)
  }
}

const shareJob = () => {
  if (navigator.share) {
    navigator.share({
      title: job.value?.title,
      text: `Check out this oilfield job: ${job.value?.title} at ${job.value?.company.companyName}`,
      url: window.location.href
    })
  } else {
    // Fallback: copy to clipboard
    navigator.clipboard.writeText(window.location.href)
    console.log('Job link copied to clipboard')
  }
}

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'posted':
      return 'badge-success'
    case 'in_progress':
      return 'badge-primary'
    case 'completed':
      return 'badge-oil'
    case 'cancelled':
      return 'badge-danger'
    default:
      return 'badge-oil'
  }
}

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

const formatCertificationType = (type: string) => {
  return type.replace('_', ' ').toUpperCase()
}

const getDaysPosted = (createdAt: Date) => {
  const days = Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
  return days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'} ago`
}

// Lifecycle
onMounted(() => {
  loadJob()
})
</script>
